import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { OnboardingStep } from '@/hooks/use-onboarding'
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/form-field'
import { isOrganisationPrefillEnabled } from '@/constants'
import { OrganisationLogoUploadField } from '@/components/account/organisation-logo-upload-field'
import { useManagedOrganisationDetail, useOrganisations } from '@/hooks/use-organisations'
import { resolveEmployerOnboardingOrgSlug } from '@/lib/employer-onboarding-organisation-api'
import { OnboardingStepNext, runOnboardingStepNext } from '@/lib/onboarding-step-nav'
import {
  importOrganisationLogo,
  searchOrganisationMatches,
  websiteDomain,
} from '@/lib/organisation-prefill-api'

interface EmployerCompanyLogoStepProps {
  step: OnboardingStep
  orgSlug?: string
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function EmployerCompanyLogoStep({
  orgSlug: orgSlugProp,
  onNext,
  onBack,
  isCompleting,
  canGoBack,
}: EmployerCompanyLogoStepProps) {
  const [orgSlug, setOrgSlug] = useState<string | undefined>(orgSlugProp)
  const [loading, setLoading] = useState(!orgSlugProp)
  const [error, setError] = useState<string | null>(null)

  const { data: organisation } = useManagedOrganisationDetail(orgSlug)
  const {
    uploadOrganisationLogo,
    deleteOrganisationLogo,
    isUploadingOrganisationLogo,
    isDeletingOrganisationLogo,
  } = useOrganisations({ managedListEnabled: false })

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const slug = orgSlugProp ?? (await resolveEmployerOnboardingOrgSlug())
        if (!slug) {
          if (!cancelled) setError('Create your organisation first, then add a logo.')
          return
        }
        if (!cancelled) setOrgSlug(slug)
      } catch (err) {
        console.error('Failed to resolve organisation:', err)
        if (!cancelled) setError('Failed to load organisation')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [orgSlugProp])

  const queryClient = useQueryClient()
  const prefillEnabled = isOrganisationPrefillEnabled()
  const hasServerLogo = Boolean(organisation?.logo_url)
  const domain = prefillEnabled ? websiteDomain(organisation?.website) : null
  const [suggestedLogo, setSuggestedLogo] = useState<{ domain: string; src: string } | null>(null)
  const [importingLogo, setImportingLogo] = useState(false)
  const logoBusy = isUploadingOrganisationLogo || isDeletingOrganisationLogo || importingLogo

  useEffect(() => {
    if (!domain || hasServerLogo) return
    const controller = new AbortController()
    searchOrganisationMatches(domain, controller.signal)
      .then((data) => {
        const match = data.results.find((result) => result.domain === domain)
        if (match?.icon_url) setSuggestedLogo({ domain, src: match.icon_url })
      })
      .catch(() => {})
    return () => controller.abort()
  }, [domain, hasServerLogo])

  const handleUseSuggestedLogo = async () => {
    if (!orgSlug || !suggestedLogo) return
    setImportingLogo(true)
    setError(null)
    try {
      const updated = await importOrganisationLogo(orgSlug, suggestedLogo.domain)
      queryClient.setQueryData(['organisation', orgSlug], updated)
      void queryClient.invalidateQueries({ queryKey: ['managed-organisations'] })
      setSuggestedLogo(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not import that logo')
    } finally {
      setImportingLogo(false)
    }
  }

  const handleContinue = async () => {
    await runOnboardingStepNext(onNext, setError)
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading your organisation…</p>
  }

  return (
    <div className="space-y-6">
      <FormField
        label="Company logo"
        description="Optional — add a logo now or continue and upload one later from organisation settings."
      >
        {orgSlug && organisation ? (
          <OrganisationLogoUploadField
            logoFieldKey={`${organisation.logo_url ?? 'no-logo'}:${organisation.updated_at}`}
            logoUrl={organisation.logo_url}
            uploadLogo={(file) => uploadOrganisationLogo({ slug: orgSlug, file })}
            deleteLogo={hasServerLogo ? () => deleteOrganisationLogo(orgSlug) : undefined}
            isUploadingLogo={isUploadingOrganisationLogo}
            isDeletingLogo={isDeletingOrganisationLogo}
            disabled={isCompleting}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            Create your organisation first, then you can upload a logo here.
          </p>
        )}
      </FormField>

      {suggestedLogo && !hasServerLogo ? (
        <div className="flex items-center gap-4 rounded-lg border border-border p-3">
          <img
            src={suggestedLogo.src}
            alt={`Logo found for ${suggestedLogo.domain}`}
            className="size-16 shrink-0 rounded-lg bg-muted/40 object-contain"
            onError={() => setSuggestedLogo(null)}
          />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-sm font-medium">Logo found for {suggestedLogo.domain}</p>
            <p className="text-xs text-muted-foreground">Use it, or upload your own above.</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isCompleting || logoBusy}
            onClick={() => void handleUseSuggestedLogo()}
          >
            {importingLogo ? 'Adding…' : 'Use this logo'}
          </Button>
        </div>
      ) : null}

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleContinue}
        backDisabled={isCompleting || logoBusy}
        continueDisabled={isCompleting || logoBusy || !orgSlug}
        isBusy={isCompleting}
      />
    </div>
  )
}
